/**
 * Composition root: builds the core, the shell, the renderers and the input
 * layer, then drives them from a single requestAnimationFrame loop. This file
 * and `app/shell.ts` are the only places that touch the wall clock.
 */

import './style.css';
import { Action, GameState, TUNING } from './core/game.ts';
import { PIECE_COLORS } from './core/piece.ts';
import { MirroredBagGenerator, SeededSevenBagGenerator } from './core/generator.ts';
import { Screen, Shell } from './app/shell.ts';
import { GameMode, GameSession } from './app/session.ts';
import { CanvasRenderer } from './render/canvas.ts';
import { BalanceRenderer } from './render/balance.ts';
import { MenuRenderer } from './render/menu.ts';
import { TwinRenderer } from './render/twin.ts';
import { Surface } from './render/surface.ts';
import { SHELLS, formatScore, type ShellTheme } from './render/theme.ts';
import { KeyboardInput } from './input/keyboard.ts';
import type { HitId, HitList } from './render/hits.ts';
import { LocalScoreboard } from './storage/localScoreboard.ts';
import { AudioDirector, readUi } from './audio/director.ts';
import { WebAudioBoard } from './audio/synth.ts';
import { Conductor } from './music/conductor.ts';
import type { VolumeLevel } from './render/painter.ts';

const canvas = document.querySelector<HTMLCanvasElement>('#game');
if (!canvas) throw new Error('#game canvas not found');

const twinSeed = Math.floor(Math.random() * 0x1_0000_0000);
const game = new GameState({ generator: new SeededSevenBagGenerator(twinSeed) });
const mirrorSource = new SeededSevenBagGenerator(twinSeed);
const twinGame = new GameState({ generator: new MirroredBagGenerator(mirrorSource) });
const session = new GameSession([game, twinGame]);
const repository = new LocalScoreboard();

// Audio is synthesised, not loaded: the board renders the cue bank through Web
// Audio on demand. The context cannot exist before a gesture, so `unlock()` is
// called from the first key or pointer press below.
const VOLUME_VALUES: Record<VolumeLevel, number> = { low: 0.3, medium: 0.6, high: 1 };
const storedVolume = repository.loadVolume();
let volumeLevel: VolumeLevel = storedVolume === 'low' || storedVolume === 'high' ? storedVolume : 'medium';
const sound = new WebAudioBoard({ muted: repository.loadAudio() === 'off', volume: VOLUME_VALUES[volumeLevel] });
const audio = new AudioDirector(sound);
audio.attach(game);
// TWIN's second board answers a step higher, so the two are told apart by ear.
audio.attach(twinGame, { pitch: 1.06 });

// The score rides its own bus under the cue bank and remembers its own switch:
// wanting the effects without the music is a different wish from wanting
// silence, so `F3` and `F4` are separate.
let musicOn = repository.loadMusic() !== 'off';
const conductor = new Conductor(sound.musicBus);
conductor.setEnabled(musicOn);
conductor.attach(game);
conductor.attach(twinGame);

const input = new KeyboardInput((action: Action) => {
  audio.beforeInput();
  session.applyInput(action);
  audio.afterInput(action);
});

const shell = new Shell({
  session,
  input,
  repository,
  persistent: repository.persistent,
  // The canvas is letterboxed, so the page behind it follows the shell too.
  onThemeChange: (theme: ShellTheme) => {
    document.body.style.background = SHELLS[theme].background;
    document.body.style.color = SHELLS[theme].text;
    document.documentElement.style.colorScheme = theme === 'night' ? 'dark' : 'light';
  },
});

const surface = new Surface(canvas);
const gameRenderer = new CanvasRenderer(surface);
const balanceRenderer = new BalanceRenderer(surface);
const twinRenderer = new TwinRenderer(surface);
const menuRenderer = new MenuRenderer(surface);

// Render reacts to core events instead of polling.
game.events.on('onLevelUp', (event) => {
  gameRenderer.flashLevelUp(event.level);
  balanceRenderer.flashLevelUp();
});

game.events.on('onLock', (event) => {
  if (!event.hardDrop || event.dropDistance <= 0) return;
  gameRenderer.showDropTrail(event.cells, event.dropDistance, event.piece.type);
  // A clear pays more and takes the plaque; the drop award only shows alone.
  if (event.lines === 0) {
    gameRenderer.showAward(
      'HARD DROP',
      event.dropDistance * TUNING.scoring.hardDropPerCell,
      `${event.dropDistance} CELLS × ${TUNING.scoring.hardDropPerCell}`,
      PIECE_COLORS[event.piece.type],
    );
  }
});

game.events.on('onLineClear', (event) => {
  const base = TUNING.scoring.lineClear[event.lines] ?? 0;
  const sub =
    event.tspin === 'none'
      ? `${formatScore(base)} × LVL ${game.level}`
      : `${event.tspin === 'mini' ? 'MINI · ' : ''}LVL ${game.level}`;
  gameRenderer.showAward(clearName(event.lines, event.tspin), event.points, sub, '#efdc38');
  if (shell.gameMode === GameMode.Solo) gameRenderer.showMultiLineClear(game, event);
  else if (shell.gameMode === GameMode.Balance) balanceRenderer.showMultiLineClear(game, event);
  else twinRenderer.showMultiLineClear(0, game, event);
});

twinGame.events.on('onLineClear', (event) => {
  if (shell.gameMode === GameMode.Twin) twinRenderer.showMultiLineClear(1, twinGame, event);
});

function clearName(lines: number, tspin: string): string {
  const name = ['ZERO', 'SINGLE', 'DOUBLE', 'TRIPLE', 'TETRIS'][lines] ?? 'CLEAR';
  return tspin === 'none' ? name : `T-SPIN ${name}`;
}

/**
 * Run an interaction and sound whatever it changed in the interface. The
 * fallback covers controls that make no visible difference here (resuming a
 * pause, say) so a pointer press is never silent.
 */
function withUiSound(run: () => void, fallback: 'ui.select' | null = null): void {
  const before = readUi(shell);
  audio.mark();
  run();
  audio.uiChanged(before, readUi(shell));
  if (fallback !== null && !audio.sounded) audio.play(fallback);
}

/** F3 mirrors F2: a global toggle that survives a reload. */
function toggleSound(): void {
  const muted = sound.toggleMuted();
  repository.saveAudio(muted ? 'off' : 'on');
  if (muted) return;
  sound.unlock();
  audio.play('ui.toggle');
}

/** F4 sits beside F3: the music switch, also surviving a reload. */
function toggleMusic(): void {
  musicOn = !musicOn;
  conductor.setEnabled(musicOn);
  repository.saveMusic(musicOn ? 'on' : 'off');
  sound.unlock();
  audio.play('ui.toggle');
}

function cycleVolume(): void {
  volumeLevel = volumeLevel === 'low' ? 'medium' : volumeLevel === 'medium' ? 'high' : 'low';
  sound.setVolume(VOLUME_VALUES[volumeLevel]);
  repository.saveVolume(volumeLevel);
  sound.unlock();
  audio.play('ui.toggle');
}

// The shell routes keys, so it owns the listeners rather than KeyboardInput.
window.addEventListener('keydown', (event) => {
  // Browsers only allow an AudioContext to start inside a gesture.
  sound.unlock();
  if (event.code === 'F3') {
    event.preventDefault();
    toggleSound();
    return;
  }
  if (event.code === 'F4') {
    event.preventDefault();
    toggleMusic();
    return;
  }
  withUiSound(() => shell.keyDown(event));
});
window.addEventListener('keyup', (event) => shell.keyUp(event));
window.addEventListener('blur', () => shell.blur());
window.addEventListener('resize', () => surface.resize());

/** Each mode has its own stage, so each has its own hit list. */
function gameHits(): HitList {
  if (shell.gameMode === GameMode.Twin) return twinRenderer.hits;
  if (shell.gameMode === GameMode.Balance) return balanceRenderer.hits;
  return gameRenderer.hits;
}

/** Which renderer's hit list is live depends on the screen that just drew. */
function hitAt(event: PointerEvent): ReturnType<typeof menuRenderer.hits.at> {
  const point = surface.toLogical(event.clientX, event.clientY);
  if (!point) return null;
  const hits = shell.screen === Screen.Game ? gameHits() : menuRenderer.hits;
  return hits.at(point.x, point.y);
}

/**
 * Restarting is the one pointer control that changes nothing the shell exposes
 * and no phase, so the director has to be told an action happened.
 */
const POINTER_ACTIONS: Partial<Record<NonNullable<HitId>, Action>> = {
  'pause.restart': Action.Restart,
  'over.restart': Action.Restart,
};

canvas.addEventListener('pointerdown', (event) => {
  if (event.button !== 0) return;
  sound.unlock();
  const id = hitAt(event);
  if (id === null) return;
  event.preventDefault();
  if (id === 'sound') {
    toggleSound();
    return;
  }
  if (id === 'volume') {
    cycleVolume();
    return;
  }
  if (id === 'music') {
    toggleMusic();
    return;
  }
  withUiSound(() => {
    audio.beforeInput();
    shell.pointerDown(id);
    const action = POINTER_ACTIONS[id];
    if (action !== undefined) audio.afterInput(action);
  }, 'ui.select');
});

window.addEventListener('pointermove', (event) => {
  const id = hitAt(event);
  surface.setPointer(id !== null);
  withUiSound(() => shell.pointerMove(id));
});

let lastTimestamp: number | null = null;

function frame(timestamp: number): void {
  const dt = lastTimestamp === null ? 0 : timestamp - lastTimestamp;
  lastTimestamp = timestamp;

  shell.step(dt);
  // Soft-drop ticks and the BALANCE warning have no event; they are diffed.
  audio.afterStep();
  // The score reads the first board in every mode: TWIN's two share a level,
  // and the arrangement already carries what makes TWIN sound like TWIN.
  conductor.step(dt, { screen: shell.screen, gameMode: shell.gameMode }, game);

  switch (shell.screen) {
    case Screen.Menu:
      menuRenderer.drawMenu(shell.menuView(), dt, sound.muted, volumeLevel, musicOn);
      break;
    case Screen.Scoreboard:
      menuRenderer.drawScoreboard(shell.scoreboardView(), dt, sound.muted, volumeLevel, musicOn);
      break;
    case Screen.Game:
      if (shell.gameMode === GameMode.Twin) {
        twinRenderer.draw(session, shell.gameView(), dt, sound.muted, volumeLevel, musicOn);
      } else if (shell.gameMode === GameMode.Balance) {
        balanceRenderer.draw(game, shell.gameView(), dt, sound.muted, volumeLevel, musicOn);
      } else gameRenderer.draw(game, shell.gameView(), dt, sound.muted, volumeLevel, musicOn);
      break;
  }

  requestAnimationFrame(frame);
}

// Dev-only handle for poking at the simulation from the console. Stripped from
// production builds by the bundler.
if (import.meta.env.DEV) {
  (window as unknown as { kd: unknown }).kd = {
    game,
    twinGame,
    session,
    shell,
    repository,
    surface,
    sound,
    audio,
    conductor,
    gameRenderer,
    balanceRenderer,
    twinRenderer,
    menuRenderer,
  };
}

// Wait for the local font so the first frame is not drawn in a fallback face.
void document.fonts?.ready.finally(() => surface.resize());

requestAnimationFrame(frame);
